import { maskString } from '@akashrajpurohit/utils';
import { BOT_FIELD_NAME } from '@lib/constants';
import {
	ErrorBoundary,
	Suspense,
	createResource,
	createSignal,
} from 'solid-js';
import { twMerge } from 'tailwind-merge';

const postFormData = async (
	formData: FormData,
): Promise<{ message: string; error: boolean }> => {
	if (formData.get(BOT_FIELD_NAME) !== '') {
		// A bot submitted the form by filling a hidden field
		window?.umami?.track('bot~newsletter-form', {
			data: formData.get(BOT_FIELD_NAME),
		});

		return { message: 'Go away 🤖', error: true };
	}

	const response = await fetch('/api/newsletter', {
		method: 'POST',
		body: JSON.stringify(Object.fromEntries(formData)),
		headers: {
			'Content-Type': 'application/json',
		},
	});
	const data = (await response.json()) as { error: boolean; message: string };

	const email = formData.get('email') as string;

	window?.umami?.track('newsletter', {
		email: data.error ? maskString(email, 6) : undefined,
	});

	return data;
};

type Props = {
	from_url?: string;
};

export default function NewsletterForm({ from_url = '' }: Props) {
	const [formData, setFormData] = createSignal<FormData>();
	const [response] = createResource(formData, postFormData);

	function submit(e: SubmitEvent) {
		e.preventDefault();
		setFormData(new FormData(e.target as HTMLFormElement));
	}

	return (
		<div class="rounded-2xl border border-zinc-200 p-6 dark:border-zinc-700">
			<h2 class="flex text-sm font-semibold text-zinc-900 dark:text-zinc-100">
				<svg
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="1.5"
					stroke="currentColor"
					class="h-5 w-5 flex-none"
				>
					<title class="sr-only">Newsletter</title>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V19.5z"
					/>
				</svg>
				<span class="ml-2">Stay up to date</span>
			</h2>
			<p class="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
				Get notified when I publish something new, and unsubscribe at any time.
			</p>
			<form
				onSubmit={submit}
				class="mt-6"
			>
				<input
					type="hidden"
					name="from_url"
					class="sr-only"
					value={from_url}
				/>
				<input
					type="hidden"
					name={BOT_FIELD_NAME}
					placeholder="do not fill this"
				/>
				<div class="relative flex w-full gap-2 sm:gap-0">
					<input
						type="email"
						name="email"
						placeholder="Email address"
						aria-label="Email address"
						required
						class="block w-full rounded-xl px-4 py-2.5 text-zinc-700 dark:text-zinc-200 placeholder-zinc-600 dark:placeholder-zinc-400 bg-transparent border border-zinc-400 dark:border-zinc-600 outline-none focus:ring-2 focus:ring-primary-700 dark:focus:ring-primary-400 sm:pr-32"
					/>
					<button
						type="submit"
						disabled={response.loading}
						class="sm:absolute sm:right-1 sm:top-1/2 sm:-translate-y-1/2 inline-flex items-center gap-2 justify-center rounded-lg py-2 px-3 text-sm font-medium outline-offset-2 transition active:transition-none bg-primary-200 hover:bg-primary-300 border-primary-300 text-zinc-900 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:border-zinc-800 dark:text-zinc-50 disabled:opacity-50 focus:ring-2 focus:ring-primary-700 dark:focus:ring-primary-400 focus:ring-offset-1 ring-offset-white dark:ring-offset-black cursor-pointer disabled:cursor-progress"
					>
						{response.loading ? (
							<span class="px-1 w-[4.2rem] flex justify-center text-zinc-200 dark:text-zinc-600">
								<svg
									fill="none"
									viewBox="0 0 24 24"
									stroke-width="1.5"
									stroke="currentColor"
									class="animate-spin h-5 w-5"
								>
									<title class="sr-only">Refreshing</title>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
									/>
								</svg>
							</span>
						) : (
							'Subscribe'
						)}
					</button>
				</div>
				<div class="mt-2">
					<ErrorBoundary
						fallback={
							<output class="ml-1 text-xs font-normal text-rose-600 dark:text-rose-400">
								Unable to load data 🥲
							</output>
						}
					>
						<Suspense
							fallback={
								<div class="flex justify-end">
									<a
										class="text-sm font-normal text-zinc-600 dark:text-zinc-400 hover:text-primary-600 dark:hover:text-primary-400"
										href="/newsletter/"
									>
										Read past newsletters →
									</a>
								</div>
							}
						>
							{!response() && (
								<div class="flex justify-end">
									<a
										class="text-sm font-normal text-zinc-600 dark:text-zinc-400 hover:text-primary-600 dark:hover:text-primary-400"
										href="/newsletter/"
									>
										Read past newsletters →
									</a>
								</div>
							)}
							{response() && (
								<div class="flex justify-between">
									<p
										class={twMerge(
											'ml-1 font-normal text-sm',
											response()?.error
												? 'text-red-600 dark:text-red-400'
												: 'text-green-600 dark:text-green-400',
										)}
									>
										{response()?.message}
									</p>
									<a
										class="text-sm font-normal text-zinc-600 dark:text-zinc-400 hover:text-primary-600 dark:hover:text-primary-400"
										href="/newsletter/"
									>
										Read past newsletters →
									</a>
								</div>
							)}
						</Suspense>
					</ErrorBoundary>
				</div>
			</form>
		</div>
	);
}
